import React from 'react';
import { useRouter, withRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import SpecialsPage from '@fuww/library/src/Layouts/SpecialsPage';
import Layout from '../components/Layout';
import Hero from '../components/Hero';
import LinkAndAnchor from '../components/LinkAndAnchor';
import Head from '../components/Head';
import messages from '../lib/messages.json';
import HomeLink from '../components/HomeLink';
import SpecialLinks from '../components/Navigation/SpecialLinks';
import { useSiteContext } from '../components/SiteContext';
import throw404 from '../lib/throw404';
import ArticleFeed from '../components/News/ArticleFeed';
import withApollo from '../lib/apollo';
import withQuery from '../lib/withQuery';
import articlesConnectionQuery
from '../lib/queries/news/articlesConnectionQuery.graphql';
import specialPageBySlugQuery
from '../lib/queries/news/specialPageBySlug.graphql';
import {
  specialsNewsListImageConfigurations,
  specialsPageImageConfigurationsDesktop,
  specialsPageImageConfigurationsMobile,
} from '../lib/imageConfigurations';
import WfbAds from '../components/Company/WfbAds';

const PER_PAGE = 12;

const premiumEbpCardSlugs = new Set([
  'werken-in-de-mode',
  'carriere',
  'carriera',
  'arbeiten-in-der-modebranche',
]);

const NewsWithQuery = withQuery(ArticleFeed);

export const SpecialNewsHeader = ({
  data,
}) => {
  if (!data?.specialPageBySlug) { return throw404(); }

  const {
    specialPageBySlug: {
      imageUrls,
      mobileImageUrls,
      title,
    },
  } = data;

  return (
    <Hero
      imageUrls={{
        mobile: mobileImageUrls,
        desktop: imageUrls,
      }}
      alt={title}
      disableRoundedCorners
    />
  );
};

SpecialNewsHeader.propTypes = {
  data: PropTypes.shape({
    specialPageBySlug: PropTypes.shape({
      links: PropTypes.arrayOf(
        PropTypes.shape({}),
      ),
      slug: PropTypes.string,
      title: PropTypes.string,
      imageUrls: PropTypes.arrayOf(PropTypes.string),
      mobileImageUrls: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
};

SpecialNewsHeader.defaultProps = {
  data: undefined,
};

const SpecialNewsHeaderWithQuery = withQuery(SpecialNewsHeader);

const Header = () => {
  const { newsboard } = useSiteContext();
  const { query: { slug } } = useRouter();

  return (
    <SpecialNewsHeaderWithQuery
      query={specialPageBySlugQuery}
      variables={{
        slug,
        newsboard,
        mobileImageConfigurations: specialsPageImageConfigurationsMobile,
        desktopImageConfigurations: specialsPageImageConfigurationsDesktop,
      }}
      errorMessage="Error loading special page"
      showLoaderWhenDataIsEmpty
    />
  );
};

const SpecialNews = ({ slug }) => {
  const { newsboard } = useSiteContext();
  const variables = {
    newsboard,
    pageviews: false,
    first: PER_PAGE,
    imageConfigurations: specialsNewsListImageConfigurations,
    specialPageIdentifiers: [
      { newsboard, slug },
    ],
  };

  return (
    <NewsWithQuery
      query={articlesConnectionQuery}
      variables={variables}
      errorMessage="Error loading news articles"
    />
  );
};

SpecialNews.propTypes = {
  slug: PropTypes.string.isRequired,
};

export const SpecialNewsPage = ({
  data,
  showPremiumEbpCards,
}) => {
  const intl = useIntl();
  if (!data?.specialPageBySlug) { return throw404(); }

  const {
    specialPageBySlug: {
      slug,
      title,
      header,
      description,
      links,
    },
  } = data;

  const titleMessage = header;
  const descriptionMessage = description;

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor route="specials-directory">
      {`${intl.formatMessage(messages.specialsDirectoryTitle)}`}
    </LinkAndAnchor>,
    <LinkAndAnchor
      route="specials"
      passHref
      params={{ slug }}
    >
      {title}
    </LinkAndAnchor>,
  ];

  return (
    <SpecialsPage
      adSlot={showPremiumEbpCards && <WfbAds limit={4} />}
      breadcrumbs={breadcrumbs}
      title={titleMessage}
      description={descriptionMessage}
      specialLinks={(
        <SpecialLinks
          links={
            links.map(({
              title: text,
              parameters,
              href,
              route,
            }) => ({
              text,
              href,
              router: route,
              route,
              params: { ...parameters },
            }))
          }
        />
      )}
    >
      <Head
        title={titleMessage}
        description={descriptionMessage}
        payload={{ slug }}
      />
      <SpecialNews
        slug={slug}
      />
    </SpecialsPage>
  );
};

SpecialNewsPage.propTypes = {
  data: PropTypes.shape({
    specialPageBySlug: PropTypes.shape({
      links: PropTypes.arrayOf(
        PropTypes.shape({}),
      ),
      slug: PropTypes.string,
      title: PropTypes.string,
      header: PropTypes.string,
      description: PropTypes.string,
    }),
  }),
  router: PropTypes.shape({
    query: PropTypes.shape({
      slug: PropTypes.string,
    }),
  }),
  showPremiumEbpCards: PropTypes.bool,
};

SpecialNewsPage.defaultProps = {
  router: {},
  data: undefined,
  showPremiumEbpCards: false,
};

const SpecialNewsPageWithQuery = withQuery(SpecialNewsPage);

const SpecialNewsPageWithSlug = ({
  router: { query: { slug } },
}) => {
  const { newsboard } = useSiteContext();

  return (
    <SpecialNewsPageWithQuery
      query={specialPageBySlugQuery}
      variables={{
        slug,
        newsboard,
        mobileImageConfigurations: specialsPageImageConfigurationsMobile,
        imageConfigurations: specialsPageImageConfigurationsDesktop,
      }}
      errorMessage="Error loading special page"
      showLoaderWhenDataIsEmpty
      showPremiumEbpCards={premiumEbpCardSlugs.has(slug)}
    />
  );
};

SpecialNewsPageWithSlug.propTypes = {
  router: PropTypes.shape({
    query: PropTypes.shape({
      slug: PropTypes.string,
    }),
  }),
};

SpecialNewsPageWithSlug.defaultProps = {
  router: {},
};

const Specials = withRouter(SpecialNewsPageWithSlug);

Specials.getLayout = (page) => (
  <Layout header={(<Header />)}>
    {page}
  </Layout>
);

export default withApollo(Specials);
